import { User, Building2, UserCheck, FileText, Award, Briefcase, BadgeCheck, UserCog, ArrowRight, FolderOpen, ClipboardList, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { useState, useEffect } from "react";
import { getUser, removeToken, removeUser, verifyToken } from "../utils/api";
import { useIsMobile } from "./ui/use-mobile";

type UserInfo = {
  id: number;
  account: string;
  username: string;
  company_name: string;
  phone: string;
  email: string;
  userType?: 'supplier' | 'expert' | 'purchaser';
  // 专家特有字段
  expert_no?: string;
  expert_name?: string;
  expert_type?: string;
  title?: string;
  position?: string;
};

export function LoginCards() {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [checking, setChecking] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ oldPassword: '', newPassword: '', confirmNewPassword: '' });
  const [pwErrors, setPwErrors] = useState<Record<string, string>>({});
  const [pwSaving, setPwSaving] = useState(false);
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
      function checkAuth() {
      // 直接从localStorage读取，不使用getUser()函数，避免任何中间层问题
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      let userData = null;
      
      if (userStr) {
        try {
          userData = JSON.parse(userStr);
        } catch (e) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
        }
      }
      
      if (userData && token) {
        setUser(userData);
        setChecking(false);
        
        // 异步验证token，但不阻塞UI显示
        verifyToken().then(isValid => {
          if (!isValid) {
            removeToken();
            removeUser();
            setUser(null);
          }
        }).catch(() => {
          // 验证失败不立即清除，可能是网络问题
        });
      } else {
        setUser(null);
        setChecking(false);
      }
    }
    
    // 立即检查一次
    checkAuth();
    
    // 监听storage事件（跨标签页）
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'user') {
        checkAuth();
      }
    };
    
    // 监听自定义事件（同窗口内）
    const handleUserLogin = () => {
      // 延迟一点确保localStorage已写入
      setTimeout(() => {
        checkAuth();
      }, 100);
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userLogin', handleUserLogin);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogin', handleUserLogin);
    };
  }, []);

  const handleLogout = () => {
    removeToken();
    removeUser();
    setUser(null);
    window.location.reload();
  };

  const openChangePassword = () => {
    setPwForm({ oldPassword: '', newPassword: '', confirmNewPassword: '' });
    setPwErrors({});
    setShowOldPw(false);
    setShowNewPw(false);
    setShowConfirmPw(false);
    setShowChangePassword(true);
  };

  const closeChangePassword = () => {
    setShowChangePassword(false);
    setPwSaving(false);
    setPwErrors({});
    setShowOldPw(false);
    setShowNewPw(false);
    setShowConfirmPw(false);
  };

  const submitChangePassword = async () => {
    const e: Record<string, string> = {};
    if (!pwForm.oldPassword.trim()) e.oldPassword = '请输入旧密码';
    if (!pwForm.newPassword) e.newPassword = '请输入新密码';
    if (!pwForm.confirmNewPassword) e.confirmNewPassword = '请再次输入新密码';
    if (pwForm.newPassword && pwForm.confirmNewPassword && pwForm.newPassword !== pwForm.confirmNewPassword) {
      e.confirmNewPassword = '两次新密码不一致';
    }
    setPwErrors(e);
    if (Object.values(e).some(Boolean)) return;

    const token = localStorage.getItem('token');
    if (!token) {
      alert('未登录，请先登录');
      return;
    }

    setPwSaving(true);
    try {
      const resp = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          old_password: pwForm.oldPassword,
          new_password: pwForm.newPassword,
        }),
      }).then(r => r.json()).catch(() => ({ ok: false, error: 'server_error' }));

      if (resp?.ok) {
        alert('密码修改成功');
        closeChangePassword();
      } else if (resp?.error === 'invalid_old_password') {
        setPwErrors(prev => ({ ...prev, oldPassword: '旧密码不正确' }));
      } else {
        alert(resp?.error || '修改失败，请稍后重试');
      }
    } finally {
      setPwSaving(false);
    }
  };

  // 如果正在检查，显示加载状态
  if (checking) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <div className="w-1 h-6 bg-[#B8860B] rounded" />
          <h3 className="text-gray-800">登录入口</h3>
        </div>
        <div className="text-center text-gray-500 py-4">加载中...</div>
      </div>
    );
  }

  // 如果用户已登录，根据用户类型显示不同内容
  if (user) {
    const userType = user.userType;
    
    // 评审专家：显示专家信息
    if (userType === 'expert') {
      return (
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-1 h-6 bg-[#B8860B] rounded" />
            <h3 className={`text-gray-800 ${isMobile ? 'text-base' : ''}`}>专家信息</h3>
          </div>
          
          {/* 自动电子评标按钮 */}
          <button
            style={{
              width: '100%',
              height: isMobile ? '60px' : '70px',
              background: 'linear-gradient(135deg, #B8860B 0%, #C9A227 100%)',
              color: 'white',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 600,
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '10px',
              boxShadow: '0 4px 12px rgba(184, 134, 11, 0.3)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseOver={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #C9A227 0%, #D4AF37 100%)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(184, 134, 11, 0.4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseOut={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #B8860B 0%, #C9A227 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 60, 140, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
            onMouseDown={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 60, 140, 0.3)';
              }
            }}
            onMouseUp={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 60, 140, 0.4)';
              }
            }}
            onClick={() => navigate('/expert-evaluation')}
          >
            <FileText style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px' }} />
            <span>自动电子评标</span>
            <ArrowRight style={{ width: isMobile ? '16px' : '18px', height: isMobile ? '16px' : '18px' }} />
          </button>
          
          {/* 欢迎语 */}
          <div className={`flex items-center justify-between ${isMobile ? 'flex-wrap gap-2' : ''}`}>
            <p className={`text-gray-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
              欢迎您，<span className="font-bold text-[#B8860B]">{user.expert_name || user.username}</span>
            </p>
            <Button
              variant="ghost"
              className={`text-[#B8860B] hover:text-[#0050b8] hover:bg-blue-50 ${isMobile ? 'text-xs h-auto px-2 py-1' : 'text-sm h-auto px-3 py-1'} logout-btn`}
              onClick={handleLogout}
            >
              退出登录
            </Button>
          </div>
          
          {/* 专家信息展示 */}
          <div className={`bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 ${isMobile ? 'p-3' : 'p-4'}`}>
            <div className={`grid ${isMobile ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
              <div className="flex items-center gap-2">
                <BadgeCheck className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-[#B8860B] flex-shrink-0`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>专家证号</p>
                  <p className={`font-medium text-gray-800 ${isMobile ? 'text-xs truncate' : 'text-sm'}`}>{user.expert_no || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <UserCog className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-[#B8860B] flex-shrink-0`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>专家类别</p>
                  <p className={`font-medium text-gray-800 ${isMobile ? 'text-xs truncate' : 'text-sm'}`}>{user.expert_type || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Award className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-[#B8860B] flex-shrink-0`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>职称</p>
                  <p className={`font-medium text-gray-800 ${isMobile ? 'text-xs truncate' : 'text-sm'}`}>{user.title || '-'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className={`${isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4'} text-[#B8860B] flex-shrink-0`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-gray-500 ${isMobile ? 'text-xs' : 'text-xs'}`}>职务</p>
                  <p className={`font-medium text-gray-800 ${isMobile ? 'text-xs truncate' : 'text-sm'}`}>{user.position || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // 采购人：显示"我的招标"按钮
    if (userType === 'purchaser') {
      const userName = user.company_name || user.username || user.account;
      return (
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-1 h-6 bg-[#B8860B] rounded" />
            <h3 className={`text-gray-800 ${isMobile ? 'text-base' : ''}`}>采购人信息</h3>
          </div>
          
          {/* 欢迎语 */}
          <div className={`flex items-center justify-between ${isMobile ? 'flex-wrap gap-2' : ''}`}>
            <p className={`text-gray-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
              欢迎您，<span className="font-bold text-[#7c3aed]">{userName}</span>
            </p>
            <Button
              variant="ghost"
              className={`text-[#7c3aed] hover:text-[#6d28d9] hover:bg-purple-50 ${isMobile ? 'text-xs h-auto px-2 py-1' : 'text-sm h-auto px-3 py-1'} logout-btn`}
              onClick={handleLogout}
            >
              退出登录
            </Button>
          </div>
          
          {/* 我的招标按钮 */}
          <button
            style={{
              width: '100%',
              height: isMobile ? '60px' : '70px',
              background: 'linear-gradient(135deg, #B8860B 0%, #C9A227 100%)',
              color: 'white',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 600,
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '10px',
              boxShadow: '0 4px 12px rgba(0, 60, 140, 0.3)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseOver={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #0050b8 0%, #0066cc 100%)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 60, 140, 0.4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseOut={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #B8860B 0%, #0050b8 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 60, 140, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
            onMouseDown={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 60, 140, 0.3)';
              }
            }}
            onMouseUp={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 60, 140, 0.4)';
              }
            }}
            onClick={() => navigate('/purchaser-bids')}
          >
            <FolderOpen style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px' }} />
            <span>我的招标</span>
            <ArrowRight style={{ width: isMobile ? '16px' : '18px', height: isMobile ? '16px' : '18px' }} />
          </button>
        </div>
      );
    }
    
    // 供应商（默认）：显示"我的投标"和"电子投标"按钮
    const userName = user.company_name || user.username || user.account;
    
    return (
      <div className="space-y-3">
        {/* Title section */}
        <div className="flex items-center space-x-3">
          <div className="w-1 h-6 bg-[#B8860B] rounded" />
          <h3 className={`text-gray-800 ${isMobile ? 'text-base' : ''}`}>用户信息</h3>
        </div>

        {/* 欢迎语 */}
        <div className={`mb-3 flex items-center justify-between ${isMobile ? 'flex-wrap gap-2 mb-4' : ''}`}>
          <p className={`text-gray-700 ${isMobile ? 'text-sm' : 'text-sm'}`}>
            欢迎您，<span className="font-bold">{userName}</span>
            <button
              type="button"
              onClick={openChangePassword}
              style={{
                marginLeft: '10px',
                color: '#B8860B',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: isMobile ? '12px' : '13px',
                padding: 0,
              }}
            >
              修改密码
            </button>
          </p>
          <Button
            variant="ghost"
            className={`text-[#B8860B] hover:text-[#0050b8] hover:bg-blue-50 ${isMobile ? 'text-xs h-auto px-2 py-1' : 'text-sm h-auto px-3 py-1'} logout-btn`}
            onClick={handleLogout}
          >
            退出登录
          </Button>
        </div>

        {/* 修改密码弹窗 */}
        {showChangePassword && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '16px',
            }}
            onClick={closeChangePassword}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '16px',
                width: '520px',
                maxWidth: '100%',
                padding: isMobile ? '20px' : '28px',
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ fontSize: '18px', fontWeight: 700, color: '#1f2937', marginBottom: '16px' }}>
                修改密码
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>旧密码</label>
                    {pwErrors.oldPassword && <span style={{ fontSize: '12px', color: '#ef4444' }}>{pwErrors.oldPassword}</span>}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showOldPw ? 'text' : 'password'}
                      value={pwForm.oldPassword}
                      onChange={(e) => setPwForm(s => ({ ...s, oldPassword: e.target.value }))}
                      style={{
                        width: '100%',
                        height: '44px',
                        borderRadius: '10px',
                        border: `2px solid ${pwErrors.oldPassword ? '#ef4444' : '#e5e7eb'}`,
                        padding: '0 44px 0 12px',
                        outline: 'none',
                        fontSize: '14px',
                      }}
                      placeholder="请输入旧密码"
                    />
                    <button
                      type="button"
                      onClick={() => setShowOldPw((v) => !v)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      aria-label={showOldPw ? '隐藏密码' : '显示密码'}
                    >
                      {showOldPw ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>新密码</label>
                    {pwErrors.newPassword && <span style={{ fontSize: '12px', color: '#ef4444' }}>{pwErrors.newPassword}</span>}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showNewPw ? 'text' : 'password'}
                      value={pwForm.newPassword}
                      onChange={(e) => setPwForm(s => ({ ...s, newPassword: e.target.value }))}
                      style={{
                        width: '100%',
                        height: '44px',
                        borderRadius: '10px',
                        border: `2px solid ${pwErrors.newPassword ? '#ef4444' : '#e5e7eb'}`,
                        padding: '0 44px 0 12px',
                        outline: 'none',
                        fontSize: '14px',
                      }}
                      placeholder="请输入新密码"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPw((v) => !v)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      aria-label={showNewPw ? '隐藏密码' : '显示密码'}
                    >
                      {showNewPw ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <label style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>确认新密码</label>
                    {pwErrors.confirmNewPassword && <span style={{ fontSize: '12px', color: '#ef4444' }}>{pwErrors.confirmNewPassword}</span>}
                  </div>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPw ? 'text' : 'password'}
                      value={pwForm.confirmNewPassword}
                      onChange={(e) => setPwForm(s => ({ ...s, confirmNewPassword: e.target.value }))}
                      style={{
                        width: '100%',
                        height: '44px',
                        borderRadius: '10px',
                        border: `2px solid ${pwErrors.confirmNewPassword ? '#ef4444' : '#e5e7eb'}`,
                        padding: '0 44px 0 12px',
                        outline: 'none',
                        fontSize: '14px',
                      }}
                      placeholder="请再次输入新密码"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPw((v) => !v)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      aria-label={showConfirmPw ? '隐藏密码' : '显示密码'}
                    >
                      {showConfirmPw ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '18px' }}>
                <button
                  type="button"
                  onClick={closeChangePassword}
                  disabled={pwSaving}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '10px',
                    border: '1px solid #d1d5db',
                    backgroundColor: 'white',
                    color: '#374151',
                    cursor: pwSaving ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                  }}
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={submitChangePassword}
                  disabled={pwSaving}
                  style={{
                    padding: '10px 18px',
                    borderRadius: '10px',
                    border: 'none',
                    backgroundColor: pwSaving ? '#9ca3af' : '#B8860B',
                    color: 'white',
                    cursor: pwSaving ? 'not-allowed' : 'pointer',
                    fontWeight: 700,
                  }}
                >
                  {pwSaving ? '保存中...' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* 两个分开的按钮 - 水平布局 */}
        <div className="flex gap-3">
          <button
            style={{
              flex: 1,
              height: isMobile ? '60px' : '70px',
              background: 'linear-gradient(135deg, #B8860B 0%, #0050b8 100%)',
              color: 'white',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 600,
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '10px',
              boxShadow: '0 4px 12px rgba(0, 60, 140, 0.3)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseOver={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #0050b8 0%, #0066cc 100%)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 60, 140, 0.4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseOut={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #B8860B 0%, #0050b8 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 60, 140, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
            onMouseDown={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 60, 140, 0.3)';
              }
            }}
            onMouseUp={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 60, 140, 0.4)';
              }
            }}
            onClick={() => navigate('//my-bids')}
          >
            <FolderOpen style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px' }} />
            <span>我的投标</span>
            <ArrowRight style={{ width: isMobile ? '16px' : '18px', height: isMobile ? '16px' : '18px' }} />
          </button>
          <button
            style={{
              flex: 1,
              height: isMobile ? '60px' : '70px',
              background: 'linear-gradient(135deg, #B8860B 0%, #0050b8 100%)',
              color: 'white',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 600,
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '8px' : '10px',
              boxShadow: '0 4px 12px rgba(0, 60, 140, 0.3)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseOver={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #0050b8 0%, #0066cc 100%)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 60, 140, 0.4)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseOut={(e) => {
              if (!isMobile) {
                e.currentTarget.style.background = 'linear-gradient(135deg, #B8860B 0%, #0050b8 100%)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 60, 140, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }
            }}
            onMouseDown={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 60, 140, 0.3)';
              }
            }}
            onMouseUp={(e) => {
              if (!isMobile) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 60, 140, 0.4)';
              }
            }}
            onClick={() => navigate('/trade-announcements')}
          >
            <ClipboardList style={{ width: isMobile ? '20px' : '24px', height: isMobile ? '20px' : '24px' }} />
            <span>电子投标</span>
            <ArrowRight style={{ width: isMobile ? '16px' : '18px', height: isMobile ? '16px' : '18px' }} />
          </button>
        </div>
      </div>
    );
  }

  // 未登录时显示登录入口，仅保留供应商和电子投标
  const userTypes = [
    {
      icon: Building2,
      title: "供应商",
      color: "from-[#0050b8] to-[#0066ff]",
      bgColor: "bg-blue-50",
      iconBg: "bg-[#B8860B]",
      showRegister: true,
      registerPath: '/register', // 供应商注册页面
    },
    {
      icon: FileText,
      title: "电子投标",
      color: "from-[#0066ff] to-[#0088ff]",
      bgColor: "bg-cyan-50",
      iconBg: "bg-[#B8860B]",
      showRegister: false,
      registerPath: '',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Title section */}
      <div className="flex items-center space-x-3">
        <div className="w-1 h-6 bg-[#B8860B] rounded" />
        <h3 className="text-gray-800">登录入口</h3>
      </div>

      {/* 2x2 Grid */}
      <div className="grid grid-cols-2 gap-4">
        {userTypes.map((type, index) => {
          const Icon = type.icon;
          return (
            <Card
              key={index}
              className={`${type.bgColor} border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-lg`}
            >
              {/* Desktop Layout */}
              <div className="hidden md:block p-4 bg-white rounded-lg h-[80px]">
                <div className="flex items-center justify-between gap-2 w-full h-full">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className={`${type.iconBg} w-12 h-12 rounded-full flex items-center justify-center shadow-md flex-shrink-0`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <span className="text-gray-700 text-sm truncate">{type.title}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {type.showRegister && type.title !== '评审人员' && (
                      <button
                        style={{
                          padding: '4px 8px',
                          backgroundColor: 'transparent',
                          color: '#B8860B',
                          fontSize: '12px',
                          fontWeight: 500,
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#dbeafe';
                          e.currentTarget.style.color = '#0050b8';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#B8860B';
                        }}
                        onClick={() => navigate(type.registerPath)}
                      >
                        注册
                      </button>
                    )}
                    <button
                      style={{
                        padding: '4px 12px',
                        backgroundColor: '#B8860B',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 500,
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0050b8'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#B8860B'}
                      onClick={() => {
                        const loginType = type.title === '供应商' ? 'supplier' : 
                                         type.title === '评审人员' ? 'expert' : 
                                         type.title === '采购人/招标人' ? 'purchaser' : 'supplier';
                        navigate(`/login?type=${loginType}`);
                      }}
                    >
                      登录
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Layout - Two rows */}
              <div className="md:hidden p-4 bg-white rounded-lg">
                <div className="flex flex-col items-center gap-3">
                  {/* Row 1: Icon + Title */}
                  <div className="flex items-center gap-2">
                    <div className={`${type.iconBg} w-10 h-10 rounded-full flex items-center justify-center shadow-md`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-gray-700 text-sm">{type.title}</span>
                  </div>
                  
                  {/* Row 2: Buttons */}
                  <div className="flex items-center gap-2 w-full">
                    {type.showRegister && type.title !== '评审人员' && (
                      <button
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          backgroundColor: 'transparent',
                          color: '#B8860B',
                          fontSize: '12px',
                          fontWeight: 500,
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#dbeafe';
                          e.currentTarget.style.color = '#0050b8';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = '#B8860B';
                        }}
                        onClick={() => navigate(type.registerPath)}
                      >
                        注册
                      </button>
                    )}
                    <button
                      style={{
                        flex: type.showRegister ? 1 : undefined,
                        width: type.showRegister ? undefined : '100%',
                        padding: '8px 16px',
                        backgroundColor: '#B8860B',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 500,
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0050b8'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#B8860B'}
                      onClick={() => {
                        const loginType = type.title === '供应商' ? 'supplier' : 
                                         type.title === '评审人员' ? 'expert' : 
                                         type.title === '采购人/招标人' ? 'purchaser' : 'supplier';
                        navigate(`/login?type=${loginType}`);
                      }}
                    >
                      登录
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
